/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable, reaction } from 'mobx';
import * as React from 'react';

import { INosLanguagePickerData } from './nos-language-picker.data';
import { INosLanguagePickerProps } from './nos-language-picker.props.autogenerated';
import BusinessSignUpDropdown from './components/sign-up-dropdown';
import { IGetAvailableLanguagesData } from '../../actions/get-available-languages.action';

/**
 * Interface for ILanguagePickerExtendedProps.
 */
export interface ILanguagePickerExtendedProps extends INosLanguagePickerProps<INosLanguagePickerData> {
    updateDropdown(newSelectValue: IGetAvailableLanguagesData): void;
    selectedValue: IGetAvailableLanguagesData;
    isSubmittedPressed: boolean;
}

/**
 * Interface for ILanguagePickerState.
 */
export interface ILanguagePickerState {
    languageId?: string;
}

/**
 * Interface for ILanguagePickerViewProps.
 */
export interface ILanguagePickerViewProps {
    className: string;
    LanguagePicker: IModuleProps;
    availableLanguages: any;
    // selectedLanguage?: any;
}

/**
 *
 * Address component.
 * @extends {React.Component<INosLanguagePickerProps<INosLanguagePickerData>, ILanguagePickerState>}
 */
class NosLanguagePicker extends React.Component<ILanguagePickerExtendedProps, ILanguagePickerState> {
    @observable private availableLanguages?: IGetAvailableLanguagesData[];

    public constructor(props: ILanguagePickerExtendedProps) {
        super(props);

        const { data } = this.props;

        this.availableLanguages = data.availableLanguages.result || [];
    }

    public componentDidMount(): void {
        // Initializing data props
        this._dataInitialize(this.props);
    }

    public shouldComponentUpdate(nextProps: ILanguagePickerExtendedProps, nextState: ILanguagePickerState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { renderView, config, selectedValue } = this.props;

        const viewProps = {
            ...this.props,
            className: config.className,
            LanguagePicker: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            availableLanguages: this._initializeDropdown(this.availableLanguages ?? [], selectedValue)
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private readonly _initializeDropdown = (
        availableLanguages: IGetAvailableLanguagesData[],
        selectedValue: IGetAvailableLanguagesData
    ) => {
        const tempOptions = availableLanguages?.map(el => {
            return {
                key: el.key,
                value: el.value
            };
        });

        return (
            <BusinessSignUpDropdown
                className={''}
                isRequired={false}
                options={tempOptions}
                isSubmittedPressed={this.props.isSubmittedPressed}
                onChange={(event: any) => this._onInputChange(event, availableLanguages)}
                selectValue={selectedValue}
                value={selectedValue?.value ?? ''}
                resources={this.props.resources}
            />
        );
    };

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLSelectElement>, availableLanguages: IGetAvailableLanguagesData[]) => {
        const newChosenLanguage = availableLanguages.find(el => el.value === event.target.value);
        if (newChosenLanguage) {
            this.props.updateDropdown(newChosenLanguage);
        }
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: ILanguagePickerExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.availableLanguages.result,
            () => {
                this.availableLanguages = data.availableLanguages.result ?? [];
            }
        );
    };
}

export default NosLanguagePicker;
